import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useSearchParam } from 'react-use'

import { TtIcon } from '@23/ui-react'

import useFeatures from '../../../../hooks/useFeatures'
import ButtonComponent from '../../../../components/ButtonComponent/ButtonComponent'
import { JoinToSpeakInfoPanel } from '../../../../../Webinar/components/JoinToSpeakInfoPanel/JoinToSpeakInfoPanel'
import JoinToSpeakNotSupportedPanel from '../../../../../Webinar/components/JoinToSpeakNotSupportedPanel/JoinToSpeakNotSupportedPanel'
import { JoinToSpeakAccess } from '../WebinarAudienceSidebarComponent/JoinToSpeak/JoinToSpeakAccess'
import { WebinarQuestionsCardComponent } from '../index'
import { JoinToSpeakIDSearchParamName } from '../../../../join-to-speak'
import { WebinarSidebarSeparator } from '../../../WebinarSidebarFeature/components'
import { initializeDevicesAsAudience } from '../../../../../Devices/useDevices'
import { useAudienceName } from '../../../../../Chat/blocs/useAudienceName'
import { useAskAudienceName } from '../../../../../Chat/components/AskAudienceName/AskAudienceNameModal'
import { useLiveDataQuery } from '../../../../../global/blocs/useLiveData'

import './WebinarInfoAudienceSidebarComponent.scss'

interface WebinarInfoAudienceSidebarProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onAskQuestion: (a: any, b: any) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  styles: any // TODO: remove
  isMobileDevice: boolean
}

const STORAGE_KEY_JOIN_TO_SPEAK_INFO = 'tt:j2s-info-seen'

const WebinarInfoAudienceSidebarComponent = ({
  onAskQuestion,
  styles,
  isMobileDevice,
}: WebinarInfoAudienceSidebarProps) => {
  const { data: liveData } = useLiveDataQuery()
  const isQuestionsEnabled = liveData?.question_mode === 'ask'

  const { HasJoinToSpeakEnabled } = useFeatures()
  const joinToSpeakID = useSearchParam(JoinToSpeakIDSearchParamName)

  const audienceName = useAudienceName()

  const canJoinToSpeak = useMemo(() => {
    return (
      liveData &&
      HasJoinToSpeakEnabled &&
      (liveData.allow_guest_speakers_p satisfies boolean)
    )
  }, [liveData, HasJoinToSpeakEnabled])

  const [currentTopPanel, setCurrentTopPanel] = useState<
    'oncamera' | 'question' | 'oncamera-intro' | 'oncamera-unsupported'
  >(joinToSpeakID ? 'oncamera' : 'question')

  useEffect(() => {
    if (joinToSpeakID) {
      void initializeDevicesAsAudience()
    }
  }, [joinToSpeakID])

  const openAskAudienceNameModal = useAskAudienceName()

  const handleOnCameraJoinClick = useCallback(async () => {
    if (!audienceName) {
      const finished = await openAskAudienceNameModal()
      if (!finished) return
    }

    const hasAlreadySeenInfo = JSON.parse(
      localStorage.getItem(STORAGE_KEY_JOIN_TO_SPEAK_INFO) ?? 'false'
    )

    if (!hasAlreadySeenInfo) {
      setCurrentTopPanel('oncamera-intro')
      return
    }

    // request permissions
    const areDevicesInitialized = await initializeDevicesAsAudience()

    if (!areDevicesInitialized) {
      setCurrentTopPanel('oncamera-intro')
      return
    }

    if (!window.isSecureContext) {
      setCurrentTopPanel('oncamera-unsupported')
      return
    }

    setCurrentTopPanel('oncamera')
  }, [audienceName, openAskAudienceNameModal])

  if (
    !liveData ||
    (liveData.enable_ondemand_p && liveData.live_status === 'previous')
  )
    return null

  if (isMobileDevice) {
    if (!isQuestionsEnabled) return null
    return (
      <WebinarQuestionsCardComponent
        onAskQuestion={onAskQuestion}
        styles={styles}
      />
    )
  }

  if (currentTopPanel === 'question') {
    return (
      <>
        {canJoinToSpeak && liveData.streaming_method !== 'hardware' && (
          <ButtonComponent
            className="go-on-camera-button"
            color="themed"
            ariaLabel="Ask your question on camera"
            onClick={handleOnCameraJoinClick}
            round
          >
            <TtIcon
              name="record"
              library="23"
              className="go-on-camera-button-icon"
            />
            <FormattedMessage defaultMessage="Go on camera" />
          </ButtonComponent>
        )}
        {isQuestionsEnabled && (
          <WebinarQuestionsCardComponent
            onAskQuestion={onAskQuestion}
            styles={styles}
          />
        )}
      </>
    )
  }

  if (!canJoinToSpeak) return null

  switch (currentTopPanel) {
    case 'oncamera':
      return (
        <>
          <JoinToSpeakAccess
            sessionId={liveData.live_id}
            onClose={() => setCurrentTopPanel('question')}
          />
          <WebinarSidebarSeparator />
        </>
      )
    case 'oncamera-intro':
      return (
        <JoinToSpeakInfoPanel
          onDismiss={() => {
            setCurrentTopPanel('question')
          }}
          onAccept={async () => {
            const areDevicesInitialized = await initializeDevicesAsAudience()

            if (!areDevicesInitialized) return

            if (!window.isSecureContext) {
              setCurrentTopPanel('oncamera-unsupported')
              return
            }

            localStorage[STORAGE_KEY_JOIN_TO_SPEAK_INFO] = JSON.stringify(true)
            setCurrentTopPanel('oncamera')
          }}
        />
      )
    case 'oncamera-unsupported':
      return (
        <JoinToSpeakNotSupportedPanel
          onDismiss={() => setCurrentTopPanel('question')}
        />
      )
    default:
  }

  return null
}

export { WebinarInfoAudienceSidebarComponent }
